import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { api } from "../../lib/api";
import PhoneField from "./PhoneField";
import { findByIso } from "../../lib/countries";
const DEFAULT_IDENTIFY = {
  prompt: "Select your role · The form reveals itself",
  auditionTag: "The Audition",
  headingLine1: "Identify",
  headingLine2: "Yourself",
  subtext: "The page is the invitation. The form is the audition. Speak plainly — we are listening for signal, not volume.",
  submitLabel: "Submit Your Case",
  successLine1: "Your file has been received.",
  successLine2: "Watch for the envelope.",
};

const DEFAULT_FIELDS = [
  { key: "name", label: "Full Name", type: "text" },
  { key: "location", label: "City & Country", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Contact", type: "phone" },
  { key: "portfolio", label: "Instagram or Portfolio", type: "text" },
  { key: "pitch", label: "One line — why does IFGL need you?", type: "text" },
];

const DEFAULT_ROLES = [
  { name: "Model", tag: "The Face", icon: "Sparkles", enabled: true },
  { name: "Designer", tag: "The Hand", icon: "Scissors", enabled: true },
  { name: "Choreographer", tag: "The Pulse", icon: "Music2", enabled: true },
  { name: "Fashion Designer", tag: "The Atelier", icon: "Shirt", enabled: true },
  { name: "Brand / Sponsor", tag: "The House", icon: "Crown", enabled: true },
  { name: "Franchise Owner", tag: "The Empire", icon: "Building2", enabled: true },
  { name: "Photographer", tag: "The Lens", icon: "Camera", enabled: true },
  { name: "MUA / Stylist", tag: "The Eye", icon: "Brush", enabled: true },
  { name: "Creative Director", tag: "The Mind", icon: "Compass", enabled: true },
  { name: "Influencer", tag: "The Voice", icon: "Megaphone", enabled: true },
  { name: "Press / Media", tag: "The Witness", icon: "Newspaper", enabled: true },
  { name: "Investor", tag: "The Patron", icon: "Coins", enabled: true },
];

const slug = (s) =>
  s.toLowerCase().replace(/\s*\/\s*/g, "-").replace(/\s+/g, "-");

const RoleApplication = () => {
  const { content, refresh } = useContent();
  const identify = { ...DEFAULT_IDENTIFY, ...(content?.identify || {}) };
  const fields = content?.form?.fields?.length ? content.form.fields : DEFAULT_FIELDS;
  const roles = (content?.roles?.length ? content.roles : DEFAULT_ROLES).filter(
    (r) => r.enabled !== false
  );

  const [selectedRole, setSelectedRole] = useState(null);
  const [form, setForm] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, ""]))
  );
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");

  const formRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll(".reveal");
    if (!els) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.15 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [roles.length]);

  const handleRole = (roleName) => {
    const next = selectedRole === roleName ? null : roleName;
    setSelectedRole(next);
    if (next) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 280);
    }
  };

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;
    setError("");
    setPhase("submitting");
    try {
      const country = findByIso(phoneCountry);
      const payload = {
        role: selectedRole,
        ...form,
      };
      // Strip the placeholder "phone" key (it isn't a real backend field) and
      // attach the structured phone fields when a number was provided.
      delete payload.phone;
      if (phoneNumber.trim()) {
        payload.phone_country = country.iso;
        payload.phone_dial = country.dial;
        payload.phone_number = phoneNumber.trim();
      }
      await api.post("/applications", payload);
      // Refresh content so the live "Applicants" counter ticks up
      refresh?.();
      setTimeout(() => setPhase("received"), 250);
      setTimeout(() => setPhase("envelope"), 2250);
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setPhase("idle");
    }
  };

  if (phase === "received" || phase === "envelope" || phase === "submitting") {
    return (
      <section
        data-testid="submission-success"
        className="relative bg-black min-h-screen flex flex-col items-center justify-center px-6 py-32"
      >
        <div className="h-px w-16 bg-red mb-12 anim-fade-in" />
        <p
          data-testid="success-line-1"
          className="text-cotton font-condensed uppercase tracking-[0.3em] text-center anim-fade-in"
          style={{ fontSize: "clamp(20px, 3vw, 38px)", fontWeight: 600 }}
        >
          {identify.successLine1}
        </p>
        {phase === "envelope" && (
          <p
            data-testid="success-line-2"
            className="anim-fade-in mt-10 text-red font-condensed uppercase tracking-[0.3em] text-center"
            style={{ fontSize: "clamp(20px, 3vw, 38px)", fontWeight: 600 }}
          >
            {identify.successLine2}
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="identify"
      data-testid="identify-section"
      className="relative bg-black border-t border-cotton/10 py-28 md:py-40 px-6 scroll-mt-8 overflow-hidden"
    >
      <div className="absolute top-0 left-6 md:left-12 w-px h-16 bg-gold/40" />

      <div className="max-w-7xl mx-auto">
        <div className="reveal flex items-center gap-4 mb-10">
          <span className="block w-10 h-px bg-red" />
          <p
            data-testid="identify-prompt"
            className="text-[11px] tracking-[0.45em] uppercase text-cotton/55 font-light"
          >
            {identify.prompt}
          </p>
        </div>

        <div
          data-testid="role-grid"
          className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {roles.map((role, idx) => {
            const selected = selectedRole === role.name;
            const num = String(idx + 1).padStart(2, "0");
            const Icon = Icons[role.icon] || Icons.Sparkles;
            return (
              <button
                key={role.name}
                type="button"
                data-testid={`role-${slug(role.name)}`}
                onClick={() => handleRole(role.name)}
                aria-pressed={selected}
                className={`role-card-3d group relative text-left h-40 md:h-44 lg:h-48 ifgl-hover ${
                  selected ? "role-card-3d--selected" : ""
                }`}
              >
                <span className="role-card-3d__sheen" aria-hidden />
                <span className="role-card-3d__edge" aria-hidden />

                {/* Red side bar */}
                <span
                  className={`absolute left-0 top-6 bottom-6 w-[2px] transition-all duration-500 origin-center z-[2] ${
                    selected
                      ? "bg-cotton/80 scale-y-100"
                      : "bg-red/70 scale-y-50 opacity-60 group-hover:scale-y-100 group-hover:opacity-100"
                  }`}
                />

                {/* Top row: icon (left) + number (right) */}
                <div className="relative z-[3] px-5 md:px-6 pt-5 md:pt-6 flex items-start justify-between">
                  <Icon
                    size={26}
                    strokeWidth={1.25}
                    className={`transition-all duration-500 ${
                      selected
                        ? "text-cotton"
                        : "text-gold/85 group-hover:text-gold group-hover:-translate-y-0.5"
                    }`}
                  />
                  <span
                    className={`font-condensed font-light text-[11px] tracking-[0.3em] tabular-nums transition-colors duration-500 ${
                      selected ? "text-cotton/80" : "text-cotton/30 group-hover:text-gold/70"
                    }`}
                  >
                    №{num}
                  </span>
                </div>

                {/* Tag (italic) */}
                <div className="relative z-[3] px-5 md:px-6 mt-1 flex items-center gap-2">
                  <span
                    className={`block h-px w-4 transition-colors duration-500 ${
                      selected ? "bg-cotton/70" : "bg-red/80 group-hover:bg-gold"
                    }`}
                  />
                  <span
                    className={`text-[10px] tracking-[0.35em] uppercase font-light italic transition-colors duration-500 ${
                      selected ? "text-cotton/85" : "text-cotton/45 group-hover:text-gold/85"
                    }`}
                  >
                    {role.tag}
                  </span>
                </div>

                {/* Role name (big at bottom) */}
                <div className="absolute bottom-5 md:bottom-6 left-5 right-5 md:left-6 md:right-6 z-[3] flex items-end justify-between gap-2">
                  <span
                    className="font-condensed font-semibold uppercase tracking-[0.05em] leading-[1.02]"
                    style={{ fontSize: "clamp(17px, 1.7vw, 24px)" }}
                  >
                    {role.name}
                  </span>
                  <span
                    className={`text-base leading-none pb-0.5 transition-all duration-500 ${
                      selected
                        ? "opacity-100 translate-x-0 text-cotton"
                        : "opacity-0 -translate-x-2 group-hover:opacity-90 group-hover:translate-x-0 text-gold"
                    }`}
                  >
                    →
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="reveal mt-20 md:mt-28">
          <div className="flex items-center gap-6 mb-6">
            <span className="block w-12 h-px bg-gold/50" />
            <span className="text-[10px] tracking-[0.5em] uppercase text-cotton/40 font-light">
              {identify.auditionTag}
            </span>
          </div>
          <h2
            data-testid="identify-heading"
            className="text-cotton font-condensed font-black uppercase leading-[0.92] tracking-[0.01em]"
            style={{ fontSize: "clamp(54px, 9vw, 168px)", fontWeight: 900 }}
          >
            {identify.headingLine1}
            <br />
            {identify.headingLine2}
            <span className="text-red">.</span>
          </h2>
          <p
            data-testid="identify-subtext"
            className="mt-10 max-w-xl text-cotton/55 font-condensed font-light italic leading-relaxed"
            style={{ fontSize: "clamp(15px, 1.2vw, 18px)" }}
          >
            {identify.subtext}
          </p>
        </div>

        <div
          ref={formRef}
          data-testid="application-wrapper"
          className={`form-collapse mt-16 ${selectedRole ? "open" : ""}`}
        >
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-10 h-px bg-red" />
              <p className="text-[11px] tracking-[0.45em] uppercase text-cotton/55 font-light">
                Application · <span className="text-gold">{selectedRole}</span>
              </p>
            </div>

            <form
              data-testid="application-form"
              onSubmit={handleSubmit}
              noValidate
              className="space-y-10"
            >
              {fields.map((f) =>
                f.type === "phone" ? (
                  <PhoneField
                    key={f.key}
                    testId={`field-${f.key}`}
                    label={f.label}
                    country={phoneCountry}
                    number={phoneNumber}
                    onCountryChange={setPhoneCountry}
                    onNumberChange={setPhoneNumber}
                  />
                ) : (
                  <Field
                    key={f.key}
                    label={f.label}
                    testId={`field-${f.key}`}
                    type={f.type || "text"}
                    value={form[f.key] || ""}
                    onChange={handleChange(f.key)}
                  />
                )
              )}

              {error && (
                <p data-testid="form-error" className="text-red text-sm tracking-wide">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="submit-case"
                className="group relative w-full overflow-hidden bg-red text-cotton px-8 py-5 font-condensed font-extrabold uppercase tracking-[0.35em] text-base md:text-lg transition-colors duration-300"
                style={{ fontWeight: 800 }}
              >
                <span className="relative z-10 inline-flex items-center justify-between w-full">
                  <span>{identify.submitLabel}</span>
                  <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>
                </span>
                <span className="absolute inset-0 bg-cotton -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
                <span className="absolute inset-0 flex items-center justify-between px-8 text-red opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10">
                  <span>{identify.submitLabel}</span>
                  <span>→</span>
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testId, value, onChange, type = "text" }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.4em] uppercase text-cotton/55 font-light mb-3">
      {label}
    </span>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      className="ifgl-input"
      autoComplete="off"
    />
  </label>
);

export default RoleApplication;
